import Big from "big.js";

/**
 * Format BV number to display with 2 decimal places
 * If the number is a whole number (e.g., 100.00), show just the integer part (100)
 * @param value - BV value as string, number, or null
 * @returns Formatted string representation
 */
export function formatNumber(value: string | number | null): string {
  if (value === null || value === undefined) return "0";

  const num = typeof value === "string" ? parseFloat(value) : value;

  if (Number.isNaN(num)) return "0";

  // Format to 2 decimal places, then strip trailing zeros
  const formatted = num.toFixed(2).replace(/\.?0+$/, "");

  return formatted || "0";
}

/**
 * Format number with smart decimal display:
 * - 12.00 → "12" (whole number)
 * - 12.01 → "12" (decimal < 0.10, round to whole)
 * - 12.09 → "12" (decimal < 0.10, round to whole)
 * - 12.10 → "12.10" (decimal >= 0.10, show with 2 decimals)
 * - 12.25 → "12.25" (decimal >= 0.10, show with 2 decimals)
 * @param value - Number value
 * @returns Formatted string representation
 */
export function formatNumberSmart(value: number): string {
  if (Number.isNaN(value) || !Number.isFinite(value)) return "0";

  // Round to 2 decimal places first
  const rounded = Math.round(value * 100) / 100;

  // Get decimal part
  const decimalPart = Math.abs(rounded % 1);

  // If decimal is 0 or less than 0.10, show as whole number
  if (decimalPart === 0 || decimalPart < 0.1) {
    return Math.round(rounded).toString();
  }

  // Otherwise, show with 2 decimal places
  return rounded.toFixed(2);
}

/**
 * Format commission-style values: up to 3 decimals, trailing zeros trimmed,
 * dangling decimal point dropped.
 *  0.003  → "0.003"
 *  2.002  → "2.002"
 *  2.000  → "2"
 *  2.100  → "2.1"
 *  2.120  → "2.12"
 *  0      → "0"
 *  NaN / ±Infinity → "0"
 * @param value - Commission value as number, string, null, or undefined
 * @returns Formatted string representation
 */
export function formatCommission(
  value: number | string | null | undefined,
): string {
  const num = typeof value === "string" ? parseFloat(value) : (value ?? 0);
  if (!Number.isFinite(num)) return "0";
  return Number(num.toFixed(3)).toString();
}

/**
 * Safely converts any numeric-ish input to BigInt
 * - NEVER throws
 * - NEVER crashes Next.js
 * - Preserves precision via big.js
 * - Truncates decimals intentionally
 * - Handles scientific notation correctly
 * - Falls back to 0n on invalid input
 */
export function safeBigInt(
  value: unknown,
  fallback: bigint = BigInt(0),
): bigint {
  try {
    // Fast path
    if (typeof value === "bigint") return value;

    // Normalize everything into Big using string
    let big: Big;

    if (value instanceof Big) {
      big = value;
    } else if (typeof value === "number") {
      if (!Number.isFinite(value)) return fallback;
      big = new Big(value.toString());
    } else if (typeof value === "string") {
      if (!value.trim()) return fallback;
      big = new Big(value.trim());
    } else if (value && typeof value === "object" && "toString" in value) {
      big = new Big(String(value));
    } else {
      return fallback;
    }

    // Truncate toward zero (explicit and predictable)
    const integer = big.round(0, Big.roundDown);

    // Convert safely via string
    return BigInt(integer.toFixed(0));
  } catch {
    // Absolutely no crashes, ever
    return fallback;
  }
}
